import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { LandingPageConfig } from '../types';
import { themes, fonts, buttonAnimations } from '../themes';
import FallbackModal from './FallbackModal';
import { getAnalyticsTracker } from '../services/analytics';
import InAppSpy from 'inapp-spy';

// Basic crawler detection for analytics
const isCrawler = (userAgent: string): boolean => {
  if (!userAgent) return false;

  const crawlerPatterns = [
    /googlebot/i,
    /bingbot/i,
    /yandexbot/i,
    /facebookexternalhit/i,
    /twitterbot/i,
    /linkedinbot/i,
    /slackbot/i,
    /discordbot/i,
    /bot\//i,
    /crawler\//i,
    /spider\//i
  ];

  return crawlerPatterns.some(pattern => pattern.test(userAgent));
};


const PageContainer = styled.div<{ theme: any }>`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  font-family: ${props => props.theme.fonts.body};
  color: ${props => props.theme.colors.text};
  position: relative;
  overflow-x: hidden;
`;

const BackgroundBlur = styled.div<{ imageUrl: string }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.imageUrl});
  background-size: cover;
  background-position: center;
  filter: blur(50px) brightness(0.3);
  opacity: 0.1;
  z-index: 0;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 200vh;
  padding: 40px 20px 0 20px;
  text-align: center;
`;

const ProfileImage = styled(motion.img)<{ cropStyle: string }>`
  width: ${props => {
    switch (props.cropStyle) {
      case 'circle-medium': return '240px';
      case 'circle-large': return '280px';
      case 'rectangle-soft': return '240px';
      default: return '240px'; // Default to medium circle size
    }
  }};
  height: ${props => {
    switch (props.cropStyle) {
      case 'circle-medium': return '240px';
      case 'circle-large': return '280px';
      case 'rectangle-soft': return '320px'; // Taller for rectangle
      default: return '240px';
    }
  }};
  border-radius: ${props => {
    switch (props.cropStyle) {
      case 'circle-medium':
      case 'circle-large':
        return '50%';
      case 'rectangle-soft':
        return '24px'; // Soft edges for rectangle
      default:
        return '50%';
    }
  }};
  object-fit: cover;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  margin-bottom: 24px;
`;

const Name = styled(motion.h1)<{ theme: any; $font: string }>`
  font-family: ${props => props.$font};
  font-size: 2.8rem;
  font-weight: 700;
  margin: 0 0 16px 0;
  color: ${props => props.theme.colors.text};
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Bio = styled(motion.p)<{ theme: any; $font: string }>`
  font-family: ${props => props.$font};
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 500px;
  margin: 0;
  white-space: pre-line;
  color: ${props => props.theme.colors.text};
  opacity: 0.9;
`;

const ButtonsContainer = styled.div<{ $isFixed: boolean; $dynamicTop?: number }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  width: 100%;
  max-width: 300px;
  pointer-events: none;
  transition: top 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  position: fixed;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  z-index: 10;

  ${props => props.$isFixed ? `
    top: 35%;
  ` : `
    top: ${props.$dynamicTop || 50}vh;
  `}
`;

const StyledButton = styled(motion.button)<{ theme: any; animation: string; $font: string }>`
  width: 100%;
  padding: 16px 32px;
  background: ${props => props.theme.colors.button};
  color: ${props => props.theme.colors.text};
  border: none;
  border-radius: 50px;
  font-family: ${props => props.$font};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  pointer-events: auto;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    background: ${props => props.theme.colors.buttonHover};
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);

    &::before {
      left: 100%;
    }
  }

  animation: ${props => buttonAnimations[props.animation as keyof typeof buttonAnimations]};
`;

// Keyframe animations

const OnlineIndicator = styled.div<{ theme: any; $font: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 12px 0 24px 0;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  opacity: 0.8;
  font-family: ${props => props.$font};
`;

const OnlineDot = styled.div<{ theme: any }>`
  width: 8px;
  height: 8px;
  background: #48bb78;
  border-radius: 50%;
  animation: onlinePulse 2s infinite;

  @keyframes onlinePulse {
    0% {
      box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.7);
      transform: scale(1);
    }
    50% {
      box-shadow: 0 0 0 4px rgba(72, 187, 120, 0);
      transform: scale(1.2);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(72, 187, 120, 0);
      transform: scale(1);
    }
  }
`;

const HeroSection = styled.div<{ theme: any }>`
  max-width: 500px;
  width: 100%;
  padding: 50px 40px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 30px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;

  /* Theme-specific adjustments */
  ${props => {
    switch (props.theme.name) {
      case 'Soft Reds':
        return `
          background: rgba(255, 235, 236, 0.15);
          border: 1px solid rgba(255, 107, 141, 0.3);
        `;
      case 'Soft Pinks':
        return `
          background: rgba(255, 245, 247, 0.15);
          border: 1px solid rgba(255, 171, 225, 0.3);
        `;
      case 'Glossy Black':
        return `
          background: rgba(26, 26, 26, 0.3);
          border: 1px solid rgba(123, 45, 93, 0.3);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
        `;
      default:
        return '';
    }
  }}
`;

const MotionContainer = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 200px; /* Space for buttons to settle below bio */
`;

interface LandingPageTemplateProps {
  config: LandingPageConfig;
  isPreview?: boolean;
  pageId?: string; // For analytics tracking on live pages
}

const INAPP_FALLBACK_EXPIRY_MS = 12 * 60 * 60 * 1000; // 12 hours
const RECENT_CLICK_WINDOW_MS = 5 * 60 * 1000; // 5 minutes - window to detect back navigation when persisted is false

const fetchCity = async (): Promise<string | null> => {
  // Primary: ipapi.co
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    const response = await fetch('https://ipapi.co/json/', { signal: controller.signal });
    clearTimeout(timeoutId);
    if (!response.ok) throw new Error('ipapi failed');
    const data = await response.json();
    if (data.city && typeof data.city === 'string') return data.city;
  } catch (error) {
    console.warn('ipapi failed:', error instanceof Error ? error.message : String(error));
  }

  // Fallback: ipwho.is
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    const response = await fetch('https://ipwho.is/', { signal: controller.signal });
    clearTimeout(timeoutId);
    if (!response.ok) throw new Error('ipwho failed');
    const data = await response.json();
    if (data.city && typeof data.city === 'string') return data.city;
  } catch (error) {
    console.warn('ipwho failed:', error instanceof Error ? error.message : String(error));
  }

  return null;
};

const LandingPageTemplate: React.FC<LandingPageTemplateProps> = ({ config, isPreview = false, pageId }) => {
  const [showFallback, setShowFallback] = useState(false);
  const [isLegitimateUser, setIsLegitimateUser] = useState(false);
  const [buttonsFixed, setButtonsFixed] = useState(true);
  const [buttonDynamicTop, setButtonDynamicTop] = useState<number>(50);
  const [city, setCity] = useState<string>('');
  const bioRef = useRef<HTMLParagraphElement>(null);
  const theme = themes[config.theme];
  const [{ isInApp }] = useState(() => InAppSpy());

  useEffect(() => {
    if (!config) return;

    if (!isPreview && pageId) {
      // Initialize analytics tracker
      const analytics = getAnalyticsTracker(pageId);

      // Check if user came back from clicking a button
      const cameFromButton = sessionStorage.getItem('cameFromButton') === 'true';

      if (cameFromButton) {
        // Clear the flag to prevent repeated triggers
        sessionStorage.removeItem('cameFromButton');

        // Small delay to show the landing page first
        setTimeout(() => setShowFallback(true), 1000);
      }

      // Track page view with comprehensive analytics
      analytics.trackPageView().catch(error => {
        console.error('Failed to track page view:', error);
      });
    }
  }, [config, isPreview, pageId]);

  // Simplified crawler protection - unlock links immediately for legitimate users
  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isBot = isCrawler(userAgent);

    if (!isBot && !isPreview) {
      // For real users, unlock links immediately with a tiny delay for UX
      setTimeout(() => {
        setIsLegitimateUser(true);
      }, 500);
    }
  }, [isPreview]);

  // Measure bio position for dynamic button placement
  useEffect(() => {
    const measureBioPosition = () => {
      if (bioRef.current) {
        const rect = bioRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const bioBottom = rect.bottom + scrollTop;

        // Rectangle images need more clearance above buttons (60px vs 40px)
        const offset = config.imageCropStyle === 'rectangle-soft' ? 60 : 40;
        const buttonY = bioBottom + offset;

        // Convert to vh for consistent positioning
        const windowHeight = window.innerHeight;
        const buttonVh = (buttonY / windowHeight) * 100;

        setButtonDynamicTop(buttonVh);
      }
    };

    // Measure after render and when content changes
    const timer = setTimeout(measureBioPosition, 100);

    // Re-measure on resize
    const handleResize = () => measureBioPosition();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [config.bio, config.imageCropStyle]);

  // Scroll detection for button positioning
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;

      // Check if the bio area is visible (when scrollTop > windowHeight * 0.3)
      const bioAreaVisible = scrollTop > windowHeight * 0.3;

      setButtonsFixed(!bioAreaVisible);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Geolocation tag fetch
  useEffect(() => {
    if (!config?.enableGeoTag || isPreview) return;

    const loadCity = async () => {
      const cacheKey = `geo_city_${pageId || config.slug || 'geo'}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        setCity(cached);
        return;
      }

      const fetchedCity = await fetchCity();
      if (fetchedCity) {
        setCity(fetchedCity);
        sessionStorage.setItem(cacheKey, fetchedCity);
      }
    };

    loadCity();
  }, [config?.enableGeoTag, config?.slug, isPreview, pageId]);

  const handleButtonClick = async (link: string, buttonId: string) => {
    if (!isPreview && link && pageId) {
      const analytics = getAnalyticsTracker(pageId);

      // Find button config for text - ALWAYS use real button data for analytics
      const button = config.buttons.find(b => b.id === buttonId);

      // Track button click with REAL button data (not placeholder)
      if (button) {
        try {
          await analytics.trackButtonClick(buttonId, button.text, button.link);
        } catch (error) {
          console.error('Failed to track button click:', error);
        }
      }

      // Store that user clicked a button for fallback detection
      sessionStorage.setItem('cameFromButton', 'true');
      if (isInApp) {
        localStorage.setItem('cameFromButton', 'true');
        localStorage.setItem('clickedAt', Date.now().toString());
      } else {
        // For non-in-app browsers (like mobile Safari), also store in localStorage
        // so the pageshow event can find the flag
        localStorage.setItem('cameFromButton', 'true');
        localStorage.setItem('clickedAt', Date.now().toString());
      }

      // Validate URL - ensure it starts with http:// or https://
      let validUrl = link;
      if (!link.startsWith('http://') && !link.startsWith('https://')) {
        validUrl = 'https://' + link;
      }

      // Add tracking parameter
      const trackedUrl = validUrl + (validUrl.includes('?') ? '&' : '?') + 'ref=landing_page';
      window.location.href = trackedUrl;
    }
  };

  // Detect back navigation using pageshow event (works for all browsers)
  useEffect(() => {
    if (isPreview) return;

    const handlePageShow = (event: Event) => {
      const pageEvent = event as { persisted?: boolean };
      const cameFromButton = localStorage.getItem('cameFromButton') === 'true';
      if (!cameFromButton) return;

      const clickedAtRaw = localStorage.getItem('clickedAt');
      const clickedAt = clickedAtRaw ? parseInt(clickedAtRaw, 10) : 0;
      
      // Check if flag is expired (stale from previous session)
      const isExpired = clickedAt > 0 ? (Date.now() - clickedAt) > INAPP_FALLBACK_EXPIRY_MS : false;
      if (isExpired) {
        localStorage.removeItem('cameFromButton');
        localStorage.removeItem('clickedAt');
        return;
      }

      // Case 1: persisted === true means page restored from cache (back navigation)
      // Case 2: persisted === false but flag exists and is recent (within 5 min) = likely back navigation in WebView
      const isBackNavigation = pageEvent.persisted === true || 
        (clickedAt > 0 && (Date.now() - clickedAt) < RECENT_CLICK_WINDOW_MS);

      if (isBackNavigation) {
        // Clear flags immediately to prevent repeat triggers
        localStorage.removeItem('cameFromButton');
        localStorage.removeItem('clickedAt');
        
        // Small delay for better UX
        setTimeout(() => setShowFallback(true), 600);
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isPreview]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <PageContainer theme={theme}>
      {config.imageUrl && (
        <BackgroundBlur imageUrl={config.imageUrl} />
      )}

      <Content>
        <HeroSection theme={theme}>
          <MotionContainer
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {config.imageUrl && (
              <ProfileImage
                src={config.imageUrl}
                alt="Profile"
                cropStyle={config.imageCropStyle}
                variants={itemVariants}
              />
            )}

            {config.displayName && (
              <Name
                theme={theme}
                $font={fonts[config.font]}
                variants={itemVariants}
              >
                {config.displayName}
              </Name>
            )}

            {!isPreview && (
              <motion.div variants={itemVariants}>
                <OnlineIndicator theme={theme} $font={fonts[config.font]}>
                  <OnlineDot theme={theme} />
                  Online now!
                </OnlineIndicator>
              </motion.div>
            )}

            {/* Temp debug for test-geo to surface city state */}
            {city && !isPreview && (
              <motion.div
                variants={itemVariants}
                style={{
                  fontSize: '0.8em',
                  fontWeight: 'normal',
                  color: theme.colors.text,
                  backgroundColor: `${theme.colors.button}1A`,
                  border: `1px solid ${theme.colors.button}4D`,
                  borderRadius: '12px',
                  padding: '4px 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginTop: '2px',
                  marginBottom: '16px',
                  fontFamily: theme.fonts.body,
                  position: 'relative',
                  zIndex: 3,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill={theme.colors.button} />
                </svg>
                I'm in {city} - 1.8 Miles away
              </motion.div>
            )}

            {config.bio && (
              <Bio ref={bioRef} theme={theme} $font={fonts[config.font]} variants={itemVariants}>
                {config.bio.length > 150 ? `${config.bio.substring(0, 150)}...` : config.bio}
              </Bio>
            )}

            <ButtonsContainer $isFixed={buttonsFixed} $dynamicTop={buttonDynamicTop}>
              {config.buttons.map((button, index) => {
                // Simple crawler protection: real users get real links, crawlers get placeholders
                const isBot = isCrawler(navigator.userAgent);
                const buttonLink = (!isBot && isLegitimateUser) ? button.link :
                  `https://example.com/placeholder-${button.id}`;

                return (
                  <StyledButton
                    key={button.id}
                    theme={theme}
                    animation={button.animation}
                    $font={fonts[config.font]}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => handleButtonClick(buttonLink, button.id)}
                  >
                    {button.text}
                  </StyledButton>
                );
              })}
            </ButtonsContainer>
          </MotionContainer>
        </HeroSection>
      </Content>

      {showFallback && !isPreview && (
        <FallbackModal
          fallback={config.fallback}
          theme={theme}
          onClose={() => setShowFallback(false)}
          pageId={pageId}
        />
      )}
    </PageContainer>
  );
};

export default LandingPageTemplate;






